/**
 * 孩子端「做一批听写」播放器 —— `/student/dictation/:sessionId`。
 *
 * ## 与家长端播放器（`ParentDictationSessionPage`）的关系
 *
 * 同一个播放引擎（`useDictationPlayer`）、同一个节奏语义（`DictationTempoConfig`）、
 * 同一份录音/TTS 选择规则。差别只在**页面给谁用**：
 *
 * | | 家长端 | 孩子端（本页） |
 * | --- | --- | --- |
 * | 主按钮 | 「开始播放」/「暂停」 | 同左（浏览器要求用户手势才能出声） |
 * | 结束 | 读完自动标「已完成」 | **必须点「提交」**（用户原话："听 10 个 → 提交"） |
 * | 退出 | 「回听写页」 | 「这次先不听了」→ **不提交**，这一批不算听过 |
 * | 批改 / 录音入口 | 有 | 没有（那是家长的事） |
 *
 * ## 为什么「提交」与「这次先不听了」是两个按钮
 *
 * 用户原话："孩子点「听写完了」只是这一轮先不听，任务还在，下次能继续。"
 * 而批量任务的进度口径是"**只算已提交**的批"（听一半退出的不算，下次还会出现）。
 * 所以：
 *   · 「提交这一批」→ 这一批的词算听过，进度往前走；
 *   · 「这次先不听了」→ 什么都不写，这些词下次重新出。
 *
 * 两个按钮的后果写在按钮下面那一行小字里 —— 孩子要能看懂"点哪个会发生什么"。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/Bits';
import { useToast } from '../../components/ui/Toast';
import {
  IconAlertTriangle,
  IconCheck,
  IconEye,
  IconPause,
  IconPlay,
  IconReplay,
  IconSkipForward,
} from '../../components/ui/Icons';
import { toErrorMessage } from '../../lib/api/error';
import * as sessionApi from '../../lib/api/dictationSessions';
import * as assignmentApi from '../../lib/api/dictationAssignments';
import { useMyStudent } from '../../lib/useMyStudent';
import { formatClock } from '../../lib/dictationPlayer';
import { toPlayerQuestions, useDictationPlayer } from '../../lib/useDictationPlayer';

export function StudentDictationSessionPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const my = useMyStudent();

  if (my.isLoading) {
    return (
      <AppShell kind="student" title="做听写">
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (!my.studentId) {
    return (
      <AppShell kind="student" title="做听写">
        <ErrorState
          message={toErrorMessage(my.error) || '没能确认你的学生档案，请稍后重试'}
          onRetry={my.refetch}
        />
      </AppShell>
    );
  }

  return <StudentDictationPlayer studentId={my.studentId} sessionId={sessionId} />;
}

function StudentDictationPlayer({
  studentId,
  sessionId,
}: {
  studentId: string;
  sessionId: string;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: ['dictation-sessions', studentId, 'detail', sessionId] as const,
    queryFn: () => sessionApi.getSession(studentId, sessionId),
    enabled: studentId !== '' && sessionId !== '',
  });

  const session = sessionQuery.data ?? null;
  const questions = session?.questions ?? null;

  /** ⚠️ 必须记忆化：每次渲染造新数组会让播放引擎重建、播放位置丢失。 */
  const playerQuestions = useMemo(() => toPlayerQuestions(questions ?? []), [questions]);
  const tempo = useMemo(
    () => session?.tempo ?? { repeatCount: 2, gapSeconds: 5, betweenWordsSeconds: 12 },
    [session],
  );

  /** 播放进度：整批只在最后发一次（批量上报，见 `updateProgress` 的注释）。 */
  const [pending, setPending] = useState<
    { questionId: string; status: 'played' | 'skipped' }[]
  >([]);
  const markProgress = useCallback((questionId: string, status: 'played' | 'skipped') => {
    setPending((prev) => [...prev, { questionId, status }]);
  }, []);

  const saveProgressQuietly = useCallback(
    async (changes: { questionId: string; status: 'played' | 'skipped' }[]) => {
      if (changes.length === 0) return;
      try {
        await sessionApi.saveProgress(studentId, sessionId, changes);
      } catch {
        /**
         * 进度不是关键路径（它只影响回看时"读过没有"的标记）：
         * 提交才是。这里失败**不打扰**孩子 —— 别让一个红条打断听写。
         */
      }
    },
    [studentId, sessionId],
  );

  const player = useDictationPlayer({
    questions: playerQuestions,
    tempo,
    loadAudio: useCallback(
      async (wordId: string) => {
        const audio = await sessionApi.getAudio(studentId, wordId);
        return audio?.dataUrl ?? null;
      },
      [studentId],
    ),
    markProgress,
    /**
     * 整轮读完**不自动提交**：用户的口径是"听完点提交"。
     * 自动提交会让"听一半退出去"也变成"听过"（那就与验收口径相反了）。
     */
    onFinish: useCallback(() => {
      toast.showToast('这一批读完了 —— 写完了就点下面的「提交这一批」', 'success');
    }, [toast]),
  });

  /**
   * ⚠️ 孩子端**不**把会话改成「进行中」。
   *
   * 那个动作走的是 `PATCH .../sessions/:sessionId`，而它是**家长的口**
   * （`requireDictationFamilyWriter`）—— 孩子发过去只会 403，而失败又是无害的，
   * 于是就成了"悄悄失败的一个请求"。孩子这里只有一条写路径：**提交**
   * （`/submit`），它一次性把会话推到 `completed` 并写 `started_at` / `finished_at`。
   */

  /** 提交这一批（= 这一批的词算听过，任务进度往前推）。 */
  const submitMutation = useMutation({
    mutationFn: async () => {
      await saveProgressQuietly(pending);
      return assignmentApi.submitBatch(studentId, sessionId);
    },
    onSuccess: async () => {
      setPending([]);
      await qc.invalidateQueries({ queryKey: ['dictation-sessions', studentId] });
      await qc.invalidateQueries({ queryKey: ['dictation-assignments', studentId] });
      toast.showToast('这一批提交好了 👍 想继续就再点一次「开始听写」', 'success');
      navigate('/student/dictation');
    },
    onError: (err: unknown) => toast.showToast(`提交失败：${toErrorMessage(err)}`, 'error'),
  });

  /** 倒计时心跳：等待期间 `snapshot.targetAt` 不变，得自己在渲染层每 250ms 重算。 */
  const [nowTick, setNowTick] = useState(() => Date.now());
  const waiting = player.snapshot.waiting;
  useEffect(() => {
    if (!waiting) return;
    setNowTick(Date.now());
    const timer = setInterval(() => setNowTick(Date.now()), 250);
    return () => clearInterval(timer);
  }, [waiting]);

  const onStartPressed = () => {
    player.start();
  };

  // ---------------------------------------------------------------- 渲染
  if (sessionQuery.isLoading) {
    return (
      <AppShell kind="student" title="做听写">
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (sessionQuery.isError || session === null) {
    return (
      <AppShell kind="student" title="做听写">
        <ErrorState
          message={`没拿到这一批听写：${toErrorMessage(sessionQuery.error)}`}
          onRetry={() => void sessionQuery.refetch()}
        />
      </AppShell>
    );
  }

  const snapshot = player.snapshot;
  const total = questions?.length ?? null;
  /**
   * ⚠️ 这一页**刻意不取"当前是哪一道题的词"**：孩子端一旦把词/拼音渲染出来，
   *    听写就变成抄写了。播放引擎只需要"第几题"（`snapshot.position`）。
   */
  const remainingSeconds =
    snapshot.targetAt === null ? null : Math.max(0, (snapshot.targetAt - nowTick) / 1000);
  const submitted = session.status === 'completed' || session.status === 'graded';

  return (
    <AppShell kind="student" title="做听写">
      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-lg font-bold text-slate-900">{session.title}</h2>
              <p className="mt-1 text-sm text-slate-500">
                共 {total === null ? '—' : total} 个词 · 每个词读 {tempo.repeatCount} 遍、间隔{' '}
                {tempo.gapSeconds} 秒
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">
              {/*
                预习入口 —— 与家长端**同一个位置（右上角）、同一个名字**。
                用户原话："家长端……右上角有一个「先预习」的功能。我觉得孩子那边
                也要加一个这个入口，在听写之前，他可以先预习，点进去预习。"
                ⚠️ 放在播放器这一页而不是任务列表：批量任务是**点「开始听写」时后端
                   才取词**的，在那之前"这批是哪 10 个词"还不存在，没东西可预习。
                   而播放器不会自动出声，所以"进来 → 预习 → 回去按开始"时机是对的。
              */}
              <Link to={`/student/dictation/${sessionId}/preview`}>
                <Button size="sm" variant="secondary" icon={<IconEye size={16} />}>
                  先预习一下
                </Button>
              </Link>
              <Badge tone={submitted ? 'success' : 'brand'}>
                {submitted ? '已提交' : '正在做'}
              </Badge>
            </div>
          </div>
        </Card>

        {questions === null ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            没拿到这一批的题目（后端字段名与预期不一致）—— 这不是"这一批没有题目"。
            <Button
              size="sm"
              variant="secondary"
              className="ml-2"
              onClick={() => void sessionQuery.refetch()}
            >
              重新取一次
            </Button>
          </p>
        ) : questions.length === 0 ? (
          <EmptyState
            icon="📝"
            title="这一批没有题目"
            description="正常情况下不会有这种会话。回去再点一次「开始听写」试试。"
            action={
              <Link to="/student/dictation" className="mt-2">
                <Button size="sm">回听写列表</Button>
              </Link>
            }
          />
        ) : (
          <>
            <Card className="bg-brand-50/60">
              <div className="text-center">
                <p className="text-xs font-medium text-brand-700">当前进度</p>
                <p className="mt-1 text-4xl font-bold text-slate-900">
                  {snapshot.position === null ? '—' : `第 ${snapshot.position} 题`}
                  <span className="ml-2 text-base font-medium text-slate-500">
                    / 共 {questions.length} 题
                  </span>
                </p>
                {/*
                  ⚠️ 孩子端**不显示拼音**（家长端才显示）。
                  拼音就是答案的一半 —— 让孩子看见拼音，"听写"就变成了"抄写"。
                */}
                <p className="mt-2 text-base text-slate-600">
                  {snapshot.finished
                    ? '这一批读完了 ✅ 写完了就点「提交这一批」'
                    : !snapshot.gesture
                      ? '准备好纸和笔，按下面的大按钮开始 —— 点了才会出声。'
                      : snapshot.waiting
                        ? `等 ${remainingSeconds === null ? '—' : remainingSeconds.toFixed(1)} 秒`
                        : snapshot.repeat === null
                          ? '正在读…'
                          : `正在读第 ${snapshot.repeat} 遍`}
                </p>

                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white">
                  <div
                    className="h-full rounded-full bg-brand-500 transition-[width] duration-300"
                    style={{ width: `${Math.round(snapshot.progress * 100)}%` }}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(snapshot.progress * 100)}
                    aria-label="听写进度"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  已进行 {formatClock(snapshot.elapsedMs / 1000)} / 约{' '}
                  {formatClock(snapshot.totalMs / 1000)}
                </p>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                {!snapshot.gesture || snapshot.finished ? (
                  <Button size="lg" icon={<IconPlay size={20} />} onClick={onStartPressed}>
                    {snapshot.finished ? '从头再放一遍' : '开始播放'}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    variant={snapshot.playing ? 'secondary' : 'primary'}
                    icon={snapshot.playing ? <IconPause size={20} /> : <IconPlay size={20} />}
                    onClick={() => player.toggle()}
                  >
                    {snapshot.playing ? '暂停' : '继续'}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  icon={<IconReplay size={18} />}
                  disabled={!snapshot.gesture || snapshot.position === null}
                  onClick={() => player.replayCurrent()}
                >
                  重播这一题
                </Button>
                <Button
                  variant="secondary"
                  icon={<IconSkipForward size={18} />}
                  disabled={!snapshot.gesture || snapshot.position === null}
                  onClick={() => player.skipCurrent()}
                >
                  跳过这一题
                </Button>
              </div>

              {!player.speechSupported && (
                <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <span className="mt-0.5 shrink-0">
                    <IconAlertTriangle size={16} />
                  </span>
                  <span>
                    这台设备/浏览器不支持语音朗读。有家长录音的词还能放出来，
                    其它的需要换一台设备，或者让家长先把这些词录一遍。
                  </span>
                </p>
              )}
              {player.audioLoading && (
                <p className="mt-3 text-center text-xs text-slate-500">正在取家长录音…</p>
              )}
              {player.audioError !== null && (
                <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <span className="mt-0.5 shrink-0">
                    <IconAlertTriangle size={16} />
                  </span>
                  <span>{player.audioError}</span>
                </p>
              )}
            </Card>

            {/* ---------- 结算：提交 / 先不听 ---------- */}
            <Card>
              <div className="space-y-2">
                {submitted ? (
                  <p className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                    <IconCheck size={18} />
                    这一批已经提交过了。
                  </p>
                ) : (
                  <>
                    <Button
                      size="lg"
                      icon={<IconCheck size={20} />}
                      loading={submitMutation.isPending}
                      onClick={() => submitMutation.mutate()}
                    >
                      提交这一批
                    </Button>
                    <p className="text-xs text-slate-500">
                      提交之后这一批的词才算听过，家长也能看到你的进度。
                    </p>
                  </>
                )}

                <div className="border-t border-slate-100 pt-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={async () => {
                      await saveProgressQuietly(pending);
                      setPending([]);
                      navigate('/student/dictation');
                    }}
                  >
                    这次先不听了
                  </Button>
                  <p className="mt-1 text-xs text-slate-500">
                    只是这一轮先不听了，任务还在；这一批**不算听过**，下次点「开始听写」还会出这些词
                    （可能换一批新的顺序）。
                  </p>
                </div>
              </div>
            </Card>

            {/* ---------- 题目网格 ----------
                ⚠️ **只显示题号，绝不显示词本身**：孩子端看到词就等于看到答案，
                这一页就不再是"听写"了。家长端那一页显示词是为了对照检查。 */}
            <Card>
              <p className="mb-2 text-sm text-slate-600">想跳到哪一题就点哪一题：</p>
              <ul className="grid grid-cols-5 gap-2 sm:grid-cols-8 lg:grid-cols-10">
                {questions.map((question) => {
                  const isCurrent = snapshot.position === question.position;
                  const played =
                    snapshot.position !== null && question.position < snapshot.position;
                  return (
                    <li key={question.id} className="min-w-0">
                      <button
                        type="button"
                        aria-label={`第 ${question.position} 题${played ? '（已听过）' : ''}`}
                        onClick={() => player.seek(question.position)}
                        className={
                          isCurrent
                            ? 'w-full rounded-xl border-2 border-brand-500 bg-brand-50 px-2 py-3 text-center text-lg font-semibold text-brand-700'
                            : played
                              ? 'w-full rounded-xl border border-slate-200 bg-slate-50 px-2 py-3 text-center text-lg font-semibold text-slate-400'
                              : 'w-full rounded-xl border border-slate-200 px-2 py-3 text-center text-lg font-semibold text-slate-700 hover:bg-slate-50'
                        }
                      >
                        {question.position}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </>
        )}

        <div className="flex flex-wrap gap-2">
          <Link to="/student/dictation">
            <Button variant="secondary" size="sm">
              回听写列表
            </Button>
          </Link>
        </div>
      </div>
    </AppShell>
  );
}
